/**
 * F-tarefas (CEO 2026-10-29) — "Tarefas".
 *
 * Todos veem "Minhas tarefas" (atrasadas, hoje, próximos 7 dias). O
 * Administrador / Super Admin vê também "Equipe": cria tarefas pontuais ou
 * recorrentes para o escritório, gerentes de chantier e técnicos (portal) e
 * acompanha o quadro (atrasadas, últimos dias, cancelar). Aviso: sino + Painel.
 */
import { useEffect, useState } from "react";
import { useSearch } from "wouter";
import { ErpShell } from "../components/layout/ErpShell.js";
import { MyTasksList, useFmtPrazo } from "../components/domain/MyTasksList.js";
import { useAuth } from "../hooks/useAuth.js";
import { useSelectedCompany } from "../hooks/useSelectedCompany.js";
import { useT } from "../i18n/index.js";
import { trpc } from "../lib/trpc.js";

const inputClass =
  "rounded-md border border-neutral-800 bg-neutral-900 px-3 py-1.5 text-sm focus:border-lime outline-none";
type RecType = "business_days" | "weekly" | "monthly_day" | "monthly_last_business";
type TFn = ReturnType<typeof useT>;
type Key = Parameters<TFn>[0];

/** Hoje em Paris "AAAA-MM-DD". */
function hojeIso(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris" }).format(new Date());
}

export default function Tasks() {
  const t = useT();
  const { user } = useAuth();
  const companiesQuery = trpc.companies.list.useQuery();
  const adminCompanies = (companiesQuery.data ?? []).filter(
    (c) =>
      user?.isSuperAdmin ||
      user?.memberships.some((m) => m.active && m.companyId === c.id && m.role === "admin"),
  );
  const isAdmin = adminCompanies.length > 0;
  // ?view=equipe (link do sino / Painel) abre direto o quadro da equipe.
  const search = useSearch();
  const [view, setView] = useState<"mine" | "team">(() =>
    new URLSearchParams(search).get("view") === "equipe" ? "team" : "mine",
  );
  useEffect(() => {
    if (new URLSearchParams(search).get("view") === "equipe") setView("team");
  }, [search]);
  const tab = (v: "mine" | "team", label: string) => (
    <button
      type="button"
      onClick={() => setView(v)}
      aria-pressed={view === v}
      className={`rounded-md px-4 py-1.5 text-sm font-semibold ${
        view === v ? "bg-lime text-on-lime" : "border border-neutral-800"
      }`}
      data-testid={`tasks-tab-${v}`}
    >
      {label}
    </button>
  );

  return (
    <ErpShell>
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold">✅ {t("tasks.title")}</h1>
            <p className="text-xs text-text-muted">{t("tasks.hint")}</p>
          </div>
          {isAdmin && (
            <div className="flex gap-2" role="group">
              {tab("mine", t("tasks.tabMine"))}
              {tab("team", t("tasks.tabTeam"))}
            </div>
          )}
        </div>
        {view === "team" && isAdmin ? <TeamBoard companies={adminCompanies} /> : <MyTasksList />}
      </div>
    </ErpShell>
  );
}

type BoardRow = {
  id: number;
  title: string;
  description: string | null;
  priority: string;
  kind: string;
  dueAt: string | null;
  recType: string | null;
  recWeekdays: number[] | null;
  recMonthDay: number | null;
  recTime: string | null;
  endDate: string | null;
  cancelledAt: string | null;
  assigneeUserId: number;
  assigneeName: string;
  assigneeRole: string | null;
  createdByName: string | null;
  overdueCount: number;
  oldestOverdueAt: string | null;
  lastDoneAt: string | null;
  recent: Array<{
    id: number;
    dueDate: string;
    status: string;
    late: boolean;
    doneAt: string | null;
    comment: string | null;
  }>;
};

export function regraDaTarefa(t: TFn, r: BoardRow, fmtPrazo: (iso: string) => string): string {
  if (r.kind === "once") return `${t("tasks.onceWord")} · ${r.dueAt ? fmtPrazo(r.dueAt) : "—"}`;
  let base = "";
  switch (r.recType as RecType) {
    case "business_days":
      base = t("tasks.rec_business_days");
      break;
    case "weekly":
      base = (r.recWeekdays ?? []).map((d) => t(`teamNotices.dayLong${d}` as Key)).join(", ");
      break;
    case "monthly_day":
      base = `${t("tasks.monthDayWord")} ${r.recMonthDay} ${t("tasks.everyMonthWord")}`;
      break;
    case "monthly_last_business":
      base = t("tasks.rec_monthly_last_business");
      break;
  }
  return `↻ ${base} · ${t("tasks.dueBy")} ${r.recTime ?? t("tasks.endOfDay")}`;
}

function TeamBoard({ companies }: { companies: Array<{ id: number; name: string }> }) {
  const t = useT();
  const utils = trpc.useUtils();
  const fmtPrazo = useFmtPrazo();
  const [companyId, setCompanyId] = useSelectedCompany(companies);
  const [showCancelled, setShowCancelled] = useState(false);
  const enabled = companyId != null;
  const assigneesQ = trpc.tasks.assignees.useQuery({ companyId: companyId ?? 0 }, { enabled });
  const boardQ = trpc.tasks.board.useQuery(
    { companyId: companyId ?? 0, includeCancelled: showCancelled },
    { enabled },
  );
  const overdueQ = trpc.tasks.teamOverdue.useQuery({ companyId: companyId ?? 0 }, { enabled });
  const cancelMut = trpc.tasks.cancel.useMutation({
    onSuccess: () => void utils.tasks.invalidate(),
  });

  const rows = (boardQ.data?.rows ?? []) as BoardRow[];
  const porPessoa = new Map<string, BoardRow[]>();
  for (const r of rows) {
    const k = r.assigneeName;
    porPessoa.set(k, [...(porPessoa.get(k) ?? []), r]);
  }
  const roleLabel = (r: string | null) => (r ? t(`teamNotices.role_${r}` as Key) : "");
  const fmtDia = (iso: string) =>
    new Date(iso.length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    });

  return (
    <div className="space-y-4">
      <label className="block text-xs">
        <span className="mb-1 block text-text-muted">{t("tasks.companyLabel")}</span>
        <select
          value={companyId ?? ""}
          onChange={(e) => setCompanyId(Number(e.target.value))}
          className={inputClass + " w-64"}
          data-testid="tasks-company"
        >
          {companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>

      {/* Atrasadas da equipe */}
      <section
        className="rounded-lg px-4 py-3"
        style={{
          background: "var(--surface-card)",
          border: "1px solid var(--border)",
          borderLeft: `5px solid ${(overdueQ.data?.total ?? 0) > 0 ? "var(--danger-500)" : "var(--success-500)"}`,
        }}
        data-testid="tasks-team-overdue"
      >
        <p className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
          {t("tasks.teamOverdueTitle")} · {overdueQ.data?.total ?? 0}
        </p>
        {(overdueQ.data?.rows ?? []).length === 0 ? (
          <p className="text-xs text-text-muted">✓ {t("tasks.teamOverdueNone")}</p>
        ) : (
          <div className="mt-1 flex flex-wrap gap-2">
            {(overdueQ.data?.rows ?? []).map((p) => (
              <span key={p.userId} className="badge b-dang">
                {p.name} · {p.count} · {t("tasks.sinceWord")} {fmtDia(p.oldestDueAt)}
              </span>
            ))}
          </div>
        )}
      </section>

      {companyId != null && (
        <NewTaskForm
          companyId={companyId}
          assignees={assigneesQ.data?.rows ?? []}
          roleLabel={roleLabel}
          startOpen={!boardQ.isLoading && rows.length === 0}
        />
      )}

      <section className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-text-muted">
            {t("tasks.boardTitle")}
          </h2>
          <label className="flex items-center gap-1.5 text-xs text-text-muted">
            <input
              type="checkbox"
              checked={showCancelled}
              onChange={(e) => setShowCancelled(e.target.checked)}
            />
            {t("tasks.showCancelled")}
          </label>
        </div>
        {rows.length === 0 && !boardQ.isLoading && (
          <p className="text-sm text-text-muted">{t("tasks.boardEmpty")}</p>
        )}
        {[...porPessoa.entries()].map(([nome, lista]) => (
          <div
            key={nome}
            className="overflow-hidden rounded-lg"
            style={{ border: "1px solid var(--border)", background: "var(--surface-card)" }}
            data-testid="tasks-person"
          >
            <div
              className="flex items-center justify-between gap-2 px-3 py-2"
              style={{ background: "var(--surface-muted)" }}
            >
              <span className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                {nome}
              </span>
              <span className="text-xs text-text-muted">{roleLabel(lista[0]!.assigneeRole)}</span>
            </div>
            <div>
              {lista.map((r, i) => (
                <div
                  key={r.id}
                  style={i > 0 ? { borderTop: "1px solid var(--border)" } : undefined}
                  className={`flex flex-wrap items-start justify-between gap-3 px-3 py-2.5 ${r.cancelledAt ? "opacity-50" : ""}`}
                  data-testid="tasks-row"
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <p className="text-sm font-semibold" style={{ color: "var(--text-strong)" }}>
                      {r.priority === "urgent" && (
                        <span className="badge b-dang mr-1.5">{t("tasks.urgent")}</span>
                      )}
                      {r.title}
                    </p>
                    <p className="text-xs text-text-muted">
                      {regraDaTarefa(t, r, fmtPrazo)}
                      {r.createdByName && ` · ${t("tasks.requestedBy")} ${r.createdByName}`}
                    </p>
                    {r.description && (
                      <p
                        className="whitespace-pre-line text-xs"
                        style={{ color: "var(--text-body)" }}
                      >
                        {r.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      {r.cancelledAt ? (
                        <span className="badge b-mut">{t("tasks.cancelledBadge")}</span>
                      ) : r.overdueCount > 0 ? (
                        <span className="badge b-dang" data-testid="tasks-row-overdue">
                          {r.overdueCount} {t("tasks.overdueWord")}
                          {r.oldestOverdueAt &&
                            ` · ${t("tasks.sinceWord")} ${fmtDia(r.oldestOverdueAt)}`}
                        </span>
                      ) : null}
                      {r.lastDoneAt && (
                        <span className="text-text-muted">
                          {t("tasks.lastDone")}: {fmtPrazo(r.lastDoneAt)}
                        </span>
                      )}
                    </div>
                    {r.recent.length > 0 && <HistoryStrip recent={r.recent} fmtDia={fmtDia} />}
                  </div>
                  {!r.cancelledAt && (
                    <button
                      type="button"
                      disabled={cancelMut.isPending}
                      onClick={() => {
                        if (window.confirm(t("tasks.cancelConfirm"))) {
                          cancelMut.mutate({ companyId: companyId!, id: r.id });
                        }
                      }}
                      className="shrink-0 rounded border border-neutral-700 px-2 py-0.5 text-[11px]"
                      data-testid="tasks-cancel"
                    >
                      {t("tasks.cancelBtn")}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

/** Últimas ocorrências: verde feita, laranja feita com atraso, vermelho atrasada. */
function HistoryStrip({
  recent,
  fmtDia,
}: {
  recent: BoardRow["recent"];
  fmtDia: (iso: string) => string;
}) {
  const t = useT();
  const cor = (o: BoardRow["recent"][number]) =>
    o.status === "done"
      ? o.late
        ? "var(--warning-500)"
        : "var(--success-500)"
      : o.status === "skipped"
        ? "var(--neutral-400)"
        : o.late
          ? "var(--danger-500)"
          : "transparent";
  const rotulo = (o: BoardRow["recent"][number]) =>
    o.status === "done"
      ? o.late
        ? t("tasks.st_doneLate")
        : t("tasks.st_done")
      : o.status === "skipped"
        ? t("tasks.st_skipped")
        : o.late
          ? t("tasks.st_overdue")
          : t("tasks.st_open");
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="tasks-history">
      <span className="mr-1 text-[11px] text-text-muted">{t("tasks.historyLabel")}:</span>
      {recent.map((o) => (
        <span
          key={o.id}
          title={`${fmtDia(o.dueDate)} · ${rotulo(o)}${o.comment ? ` · ${o.comment}` : ""}`}
          aria-label={`${fmtDia(o.dueDate)} · ${rotulo(o)}`}
          className="inline-flex h-5 min-w-9 items-center justify-center rounded px-1 text-[10px] font-semibold"
          style={{
            background: cor(o),
            border: `1px solid ${cor(o) === "transparent" ? "var(--info-500)" : cor(o)}`,
            color: cor(o) === "transparent" ? "var(--info-500)" : "#fff",
          }}
        >
          {fmtDia(o.dueDate)}
        </span>
      ))}
    </div>
  );
}

function NewTaskForm({
  companyId,
  assignees,
  roleLabel,
  startOpen,
}: {
  companyId: number;
  assignees: Array<{ id: number; name: string; role: string }>;
  roleLabel: (r: string | null) => string;
  startOpen: boolean;
}) {
  const t = useT();
  const utils = trpc.useUtils();
  const [aberto, setAberto] = useState(false);
  const [assignee, setAssignee] = useState<number | "">("");
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [urgente, setUrgente] = useState(false);
  const [kind, setKind] = useState<"once" | "recurring">("recurring");
  const [dueAt, setDueAt] = useState(() => `${hojeIso()}T18:00`);
  const [recType, setRecType] = useState<RecType>("business_days");
  const [dias, setDias] = useState<number[]>([1, 3, 5]);
  const [monthDay, setMonthDay] = useState(1);
  const [recTime, setRecTime] = useState("");
  const [startDate, setStartDate] = useState(hojeIso);
  const [endDate, setEndDate] = useState("");
  const [ok, setOk] = useState<string | null>(null);
  const createMut = trpc.tasks.create.useMutation({
    onSuccess: () => {
      setTitle("");
      setDesc("");
      setUrgente(false);
      setOk(t("tasks.created"));
      void utils.tasks.invalidate();
    },
  });
  const open = aberto || startOpen;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => {
          setAberto(true);
          setOk(null);
        }}
        className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime"
        data-testid="tasks-new"
      >
        + {t("tasks.newTitle")}
      </button>
    );
  }

  const grupos: Array<{ label: string; roles: string[] }> = [
    { label: "office", roles: ["admin", "user", "viewer"] },
    { label: "manager", roles: ["manager"] },
    { label: "technician", roles: ["technician"] },
  ];
  const podeCriar =
    assignee !== "" &&
    title.trim().length >= 3 &&
    (kind === "once" ? dueAt !== "" : recType !== "weekly" || dias.length > 0) &&
    !createMut.isPending;

  return (
    <section
      className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900 p-4"
      data-testid="tasks-form"
    >
      <h2 className="text-sm font-semibold">{t("tasks.newTitle")}</h2>
      <div className="flex flex-wrap items-end gap-3">
        <label className="block text-xs">
          <span className="mb-1 block text-text-muted">{t("tasks.personLabel")}</span>
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value ? Number(e.target.value) : "")}
            className={inputClass + " w-64"}
            data-testid="tasks-assignee"
          >
            <option value="">{t("tasks.personPh")}</option>
            {grupos.map((g) => {
              const pessoas = assignees.filter((a) => g.roles.includes(a.role));
              if (pessoas.length === 0) return null;
              return (
                <optgroup
                  key={g.label}
                  label={g.label === "office" ? t("tasks.groupOffice") : roleLabel(g.label)}
                >
                  {pessoas.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {g.label === "office" ? ` · ${roleLabel(a.role)}` : ""}
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </label>
        <label className="block min-w-64 flex-1 text-xs">
          <span className="mb-1 block text-text-muted">{t("tasks.titleLabel")}</span>
          <input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setOk(null);
            }}
            maxLength={200}
            placeholder={t("tasks.titlePh")}
            className={inputClass + " w-full"}
            data-testid="tasks-title"
          />
        </label>
      </div>
      <textarea
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        rows={2}
        maxLength={2000}
        placeholder={t("tasks.descLabel")}
        className={inputClass + " w-full"}
        data-testid="tasks-desc"
      />
      <div className="flex flex-wrap items-end gap-3">
        <div className="block text-xs">
          <span className="mb-1 block text-text-muted">{t("tasks.kindLabel")}</span>
          <div className="flex gap-1" role="group">
            {(["recurring", "once"] as const).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={kind === k}
                onClick={() => setKind(k)}
                className={`h-8 rounded-md border px-3 text-xs font-semibold ${
                  kind === k ? "border-lime bg-lime text-on-lime" : "border-neutral-700"
                }`}
                data-testid={`tasks-kind-${k}`}
              >
                {k === "once" ? t("tasks.kindOnce") : t("tasks.kindRecurring")}
              </button>
            ))}
          </div>
        </div>
        {kind === "once" ? (
          <label className="block text-xs">
            <span className="mb-1 block text-text-muted">{t("tasks.dueAtLabel")}</span>
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className={inputClass}
              data-testid="tasks-due"
            />
          </label>
        ) : (
          <>
            <label className="block text-xs">
              <span className="mb-1 block text-text-muted">{t("tasks.repeatLabel")}</span>
              <select
                value={recType}
                onChange={(e) => setRecType(e.target.value as RecType)}
                className={inputClass + " w-64"}
                data-testid="tasks-rec"
              >
                {(["business_days", "weekly", "monthly_day", "monthly_last_business"] as const).map(
                  (r) => (
                    <option key={r} value={r}>
                      {t(`tasks.rec_${r}` as Key)}
                    </option>
                  ),
                )}
              </select>
            </label>
            {recType === "monthly_day" && (
              <label className="block text-xs">
                <span className="mb-1 block text-text-muted">{t("tasks.monthDayLabel")}</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  value={monthDay}
                  onChange={(e) =>
                    setMonthDay(Math.min(31, Math.max(1, Number(e.target.value) || 1)))
                  }
                  className={inputClass + " w-20"}
                  data-testid="tasks-monthday"
                />
              </label>
            )}
            <label className="block text-xs">
              <span className="mb-1 block text-text-muted">{t("tasks.timeLabel")}</span>
              <input
                type="time"
                value={recTime}
                onChange={(e) => setRecTime(e.target.value)}
                className={inputClass}
                data-testid="tasks-time"
              />
              <span className="mt-1 block text-[11px] text-text-muted">{t("tasks.timeHint")}</span>
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-text-muted">{t("tasks.startLabel")}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputClass}
                data-testid="tasks-start"
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block text-text-muted">{t("tasks.endLabel")}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputClass}
                data-testid="tasks-end"
              />
            </label>
          </>
        )}
      </div>
      {kind === "recurring" && recType === "weekly" && (
        <div className="flex flex-wrap gap-1" role="group" aria-label={t("tasks.rec_weekly")}>
          {[1, 2, 3, 4, 5, 6, 7].map((dia) => {
            const on = dias.includes(dia);
            return (
              <button
                key={dia}
                type="button"
                aria-pressed={on}
                onClick={() => setDias((v) => (on ? v.filter((x) => x !== dia) : [...v, dia]))}
                className={`h-8 min-w-8 rounded-md border px-2 text-xs font-semibold ${
                  on ? "border-lime bg-lime text-on-lime" : "border-neutral-700"
                }`}
                data-testid={`tasks-day-${dia}`}
              >
                {t(`teamNotices.dayLong${dia}` as Key)}
              </button>
            );
          })}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-1.5 text-xs">
          <input
            type="checkbox"
            checked={urgente}
            onChange={(e) => setUrgente(e.target.checked)}
            data-testid="tasks-urgent"
          />
          {t("tasks.urgentLabel")}
        </label>
        <button
          type="button"
          disabled={!podeCriar}
          onClick={() =>
            createMut.mutate({
              companyId,
              assigneeUserId: Number(assignee),
              title: title.trim(),
              description: desc.trim() || undefined,
              priority: urgente ? "urgent" : "normal",
              kind,
              ...(kind === "once"
                ? { dueAt: new Date(dueAt) }
                : {
                    recType,
                    recWeekdays: recType === "weekly" ? dias : undefined,
                    recMonthDay: recType === "monthly_day" ? monthDay : undefined,
                    recTime: recTime || undefined,
                    startDate: startDate || undefined,
                    endDate: endDate || undefined,
                  }),
            })
          }
          className="rounded-md bg-lime px-4 py-2 text-sm font-semibold text-on-lime disabled:opacity-40"
          data-testid="tasks-create"
        >
          ✅ {createMut.isPending ? "…" : t("tasks.createBtn")}
        </button>
        {!startOpen && (
          <button
            type="button"
            onClick={() => setAberto(false)}
            className="text-xs underline text-text-muted"
          >
            {t("tasks.backBtn")}
          </button>
        )}
      </div>
      {createMut.error && <p className="text-xs text-red-500">{createMut.error.message}</p>}
      {ok && (
        <p
          className="text-xs font-semibold"
          style={{ color: "var(--success-500)" }}
          data-testid="tasks-ok"
        >
          ✓ {ok}
        </p>
      )}
    </section>
  );
}
